import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Plus, X, RotateCcw, Save, FileText, Images, ArrowLeft, ArrowRight } from "lucide-react";
import { API, assetUrl, useSite, DEFAULT_SITE, TILE_ORDER } from "@/content";
import { CATEGORIES } from "@/data/products";

const upload = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
  return data;
};

const card = "rounded-[2rem] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-8";
const inputCls =
  "w-full rounded-full border border-ink/10 bg-sand/60 px-5 py-3 text-sm text-ink placeholder:text-ink/40 outline-none transition-colors duration-300 focus:border-ink/40 focus:bg-white";
const btnInk =
  "flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 text-[13px] font-semibold text-white transition duration-300 hover:scale-[1.02] hover:bg-ink/90 disabled:opacity-60";
const btnGhost =
  "flex items-center justify-center gap-2 rounded-full border border-ink/10 bg-white px-5 py-2.5 text-[12px] font-semibold text-ink transition duration-300 hover:bg-sand";

const UploadButton = ({ onDone, accept = "image/*", label = "Upload new", testId }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const data = await upload(f);
      onDone(data.url, f.type.startsWith("video") ? "video" : "image", data.size);
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label data-testid={testId} className={`${btnGhost} cursor-pointer ${busy ? "opacity-60" : ""}`}>
      <input type="file" accept={accept} className="hidden" onChange={pick} disabled={busy} />
      <Upload className="h-3.5 w-3.5" />
      {busy ? "Uploading…" : label}
    </label>
  );
};

const PoolPicker = ({ pool, onPick, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block">
      <button type="button" className={btnGhost} data-testid={testId} onClick={() => setOpen(!open)}>
        <Images className="h-3.5 w-3.5" /> Choose existing
      </button>
      {open && (
        <span className="absolute right-0 top-full z-40 mt-2 block w-80 rounded-2xl bg-white p-3 shadow-[0_24px_60px_rgb(0,0,0,0.18)]" data-testid={`${testId}-panel`}>
          <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.14em] text-ink/40">
            This product's photos — tap to use
          </span>
          <span className="grid max-h-64 grid-cols-4 gap-2 overflow-y-auto">
            {pool.map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => { onPick(u); setOpen(false); }}
                data-testid={`${testId}-opt-${pool.indexOf(u)}`}
                className="overflow-hidden rounded-lg bg-sand transition hover:ring-2 hover:ring-ink"
              >
                <img src={assetUrl(u)} alt="" className="h-16 w-full object-cover" />
              </button>
            ))}
          </span>
        </span>
      )}
    </span>
  );
};

const PhotoSlot = ({ label, current, isCustom, onUpload, onReset, pool, onPick, testId }) => (
  <div className="flex items-center gap-4 rounded-2xl bg-sand/60 p-3" data-testid={testId}>
    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-white">
      {current ? <img src={assetUrl(current)} alt={label} className="h-full w-full object-cover" /> : null}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[13px] font-semibold text-ink">{label}</p>
      <p className="text-[11px] text-ink/45">{isCustom ? "Custom" : "Default"}</p>
    </div>
    {pool && <PoolPicker pool={pool} onPick={onPick} testId={`${testId}-pick`} />}
    <UploadButton testId={`${testId}-upload`} onDone={(url) => onUpload(url)} />
    {isCustom && (
      <button type="button" onClick={onReset} data-testid={`${testId}-reset`} className={`${btnGhost} !px-3.5`} aria-label={`Reset ${label}`}>
        <RotateCcw className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

const PdfSlot = ({ label, value, onUpload, onRemove, testId }) => (
  <div className="flex items-center gap-4 rounded-2xl bg-sand/60 p-3" data-testid={testId}>
    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-ink text-white">
      <FileText className="h-6 w-6" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[13px] font-semibold text-ink">{label}</p>
      <p className="text-[11px] text-ink/45">
        {value
          ? `Uploaded${value.size ? ` · ${(value.size / (1024 * 1024)).toFixed(1)} MB` : ""} — shown on the product page`
          : "None — the Detailed Spec Sheet button stays hidden"}
      </p>
    </div>
    <UploadButton testId={`${testId}-upload`} accept="application/pdf" label="Upload PDF" onDone={(url, _t, size) => onUpload({ url, size })} />
    {value && (
      <button type="button" onClick={onRemove} data-testid={`${testId}-reset`} className={`${btnGhost} !px-3.5`} aria-label={`Remove ${label}`}>
        <RotateCcw className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

const GalleryEditor = ({ label, photos, onChange, testId, pool }) => {
  const move = (i, d) => {
    const a = [...photos];
    const j = i + d;
    if (j < 0 || j >= a.length) return;
    [a[i], a[j]] = [a[j], a[i]];
    onChange(a);
  };
  return (
    <div className="rounded-2xl bg-sand/60 p-4" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] font-semibold text-ink">{label}</p>
        <div className="flex gap-2">
          {pool && <PoolPicker pool={pool} onPick={(u) => onChange([...(photos || []), u])} testId={`${testId}-pick`} />}
          <UploadButton
            testId={`${testId}-add`}
            label="Upload new"
            onDone={(url) => onChange([...(photos || []), url])}
          />
        </div>
      </div>
      {photos?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {photos.map((u, i) => (
            <div key={`${u}-${i}`} className="group relative h-20 w-20 overflow-hidden rounded-xl bg-white" data-testid={`${testId}-item-${i}`}>
              <img src={assetUrl(u)} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-gradient-to-t from-ink/60 to-transparent pb-1 pt-3">
                <button type="button" onClick={() => move(i, -1)} data-testid={`${testId}-left-${i}`} className="flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-ink" aria-label="Move left">
                  <ArrowLeft className="h-3 w-3" />
                </button>
                <button type="button" onClick={() => move(i, 1)} data-testid={`${testId}-right-${i}`} className="flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-ink" aria-label="Move right">
                  <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => onChange(photos.filter((_, x) => x !== i))}
                data-testid={`${testId}-remove-${i}`}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-ink/70 text-white"
                aria-label="Remove photo"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HeroEditor = () => {
  const { site, refresh } = useSite();
  const [form, setForm] = useState(null);
  const [media, setMedia] = useState(null);
  const [saving, setSaving] = useState(false);
  const f = form || { heroEyebrow: site.heroEyebrow, heroLine1: site.heroLine1, heroLine2: site.heroLine2, heroSub: site.heroSub };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      if (media) {
        payload.heroMedia = media.url;
        payload.heroMediaType = media.type;
      }
      await axios.put(`${API}/admin/content/site`, payload, { withCredentials: true });
      toast.success("Hero updated — live on the site now.");
      setMedia(null);
      refresh();
    } catch (e) {
      toast.error("Could not save hero.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={card} data-testid="editor-hero">
      <h3 className="font-display text-2xl font-medium tracking-tight text-ink">Home page hero</h3>
      <p className="mt-1 text-xs text-ink/50">The master photo or video and text at the top of the home page.</p>
      <div className="mt-5 grid gap-6 md:grid-cols-2">
        <div>
          <div className="relative h-48 overflow-hidden rounded-2xl bg-ink/5">
            {(media?.url || site.heroMedia) &&
              ((media?.type || site.heroMediaType) === "video" ? (
                <video src={assetUrl(media?.url || site.heroMedia)} muted loop autoPlay playsInline className="h-full w-full object-cover" data-testid="hero-preview" />
              ) : (
                <img src={assetUrl(media?.url || site.heroMedia)} alt="Hero preview" className="h-full w-full object-cover" data-testid="hero-preview" />
              ))}
          </div>
          <div className="mt-3">
            <UploadButton
              testId="hero-media-upload"
              accept="image/jpeg,image/png,image/webp,video/mp4"
              label="Upload photo or video"
              onDone={(url, type) => setMedia({ url, type })}
            />
          </div>
        </div>
        <div className="space-y-3">
          <input className={inputCls} value={f.heroEyebrow} data-testid="hero-eyebrow-input" placeholder="Eyebrow (small top text)" onChange={(e) => setForm({ ...f, heroEyebrow: e.target.value })} />
          <input className={inputCls} value={f.heroLine1} data-testid="hero-line1-input" placeholder="Headline line 1" onChange={(e) => setForm({ ...f, heroLine1: e.target.value })} />
          <input className={inputCls} value={f.heroLine2} data-testid="hero-line2-input" placeholder="Headline line 2" onChange={(e) => setForm({ ...f, heroLine2: e.target.value })} />
          <textarea className={`${inputCls} !rounded-2xl`} rows={3} value={f.heroSub} data-testid="hero-sub-input" placeholder="Intro sentence" onChange={(e) => setForm({ ...f, heroSub: e.target.value })} />
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="hero-save-btn" className={`${btnInk} mt-5`}>
        <Save className="h-4 w-4" />
        {saving ? "Saving…" : "Save hero"}
      </button>
    </div>
  );
};

const TileEditor = () => {
  const { site, refresh } = useSite();
  const change = async (label, url) => {
    try {
      await axios.put(`${API}/admin/content/site`, { tiles: { ...site.tiles, [label]: url } }, { withCredentials: true });
      toast.success(`${label} tile updated.`);
      refresh();
    } catch (e) {
      toast.error("Could not update tile.");
    }
  };
  return (
    <div className={card} data-testid="editor-tiles">
      <h3 className="font-display text-2xl font-medium tracking-tight text-ink">Collection tile photos</h3>
      <p className="mt-1 text-xs text-ink/50">The five large photos in the home page "One considered language" section.</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {TILE_ORDER.map((label) => (
          <PhotoSlot
            key={label}
            label={label}
            testId={`tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
            current={site.tiles[label]}
            isCustom={site.tiles[label] !== DEFAULT_SITE.tiles[label]}
            onUpload={(url) => change(label, url)}
            onReset={() => change(label, DEFAULT_SITE.tiles[label])}
          />
        ))}
      </div>
    </div>
  );
};

const FeaturedEditor = () => {
  const { site, products, refresh } = useSite();
  const [ids, setIds] = useState(null);
  const [saving, setSaving] = useState(false);
  const current = ids || site.featuredIds;
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/site`, { featuredIds: current }, { withCredentials: true });
      toast.success("Featured pieces updated.");
      refresh();
    } catch (e) {
      toast.error("Could not save featured pieces.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={card} data-testid="editor-featured">
      <h3 className="font-display text-2xl font-medium tracking-tight text-ink">Featured pieces</h3>
      <p className="mt-1 text-xs text-ink/50">The four products highlighted on the home page.</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {current.map((id, i) => (
          <select
            key={i}
            value={id}
            data-testid={`featured-slot-${i}`}
            onChange={(e) => setIds(current.map((x, xi) => (xi === i ? e.target.value : x)))}
            className={`${inputCls} cursor-pointer`}
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.sku ? `(${p.sku})` : ""}
              </option>
            ))}
          </select>
        ))}
      </div>
      <button onClick={save} disabled={saving || !ids} data-testid="featured-save-btn" className={`${btnInk} mt-5`}>
        <Save className="h-4 w-4" />
        {saving ? "Saving…" : "Save featured pieces"}
      </button>
    </div>
  );
};

const ProductEditor = () => {
  const { products, overrides, refresh } = useSite();
  const [pid, setPid] = useState("");
  const [form, setForm] = useState({ name: "", sku: "", blurb: "" });
  const [specsOn, setSpecsOn] = useState(false);
  const [specRows, setSpecRows] = useState([]);
  const [images, setImages] = useState({});
  const [specPdf, setSpecPdf] = useState(null);
  const [specPdfs, setSpecPdfs] = useState({});
  const [saving, setSaving] = useState(false);

  const p = useMemo(() => products.find((x) => x.id === pid), [products, pid]);
  const ov = overrides[pid] || {};

  const pool = useMemo(() => {
    if (!p) return [];
    const effFi = { ...(p.finishImages || {}), ...(images.finishImages || {}) };
    const effFg = { ...(p.finishGalleries || {}), ...(images.finishGalleries || {}) };
    const all = [
      images.image ?? p.image,
      ...Object.values(effFi),
      ...Object.values(effFg).flat(),
      ...(images.galleryImages ?? p.galleryImages ?? []),
    ].filter(Boolean);
    return [...new Set(all)];
  }, [p, images]);

  const select = (id) => {
    setPid(id);
    const o = overrides[id] || {};
    setForm({ name: o.name || "", sku: o.sku || "", blurb: o.blurb || "" });
    setSpecsOn(!!o.extraSpecs);
    setSpecRows(o.extraSpecs || []);
    setImages(o.images || {});
    setSpecPdf(o.specPdf ? { url: o.specPdf, size: o.specPdfSize } : null);
    setSpecPdfs(o.specPdfs || {});
  };

  const setImg = (key, value) => setImages((im) => ({ ...im, [key]: value }));
  const setFinishImg = (finish, value) =>
    setImages((im) => ({ ...im, finishImages: { ...(im.finishImages || {}), [finish]: value } }));
  const setFinishGal = (finish, arr) =>
    setImages((im) => ({ ...im, finishGalleries: { ...(im.finishGalleries || {}), [finish]: arr } }));

  const save = async () => {
    if (!p) return;
    const payload = {};
    if (form.name && form.name !== p.name) payload.name = form.name;
    if (form.sku && form.sku !== (p.sku || "")) payload.sku = form.sku;
    if (form.blurb && form.blurb !== (p.blurb || "")) payload.blurb = form.blurb;
    if (specsOn) payload.extraSpecs = specRows.filter((r) => r.l.trim());
    if (Object.keys(images).length) payload.images = images;
    if ((p.finishes || []).length > 1) {
      payload.specPdfs = specPdfs; // per-finish PDFs; replaced wholesale so removals persist
    } else if (specPdf) {
      payload.specPdf = specPdf.url;
      if (specPdf.size) payload.specPdfSize = specPdf.size;
    } else if (ov.specPdf) {
      payload.specPdf = ""; // clear the uploaded PDF
    }
    if (!Object.keys(payload).length) {
      toast.message("No changes to save.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/products/${pid}`, payload, { withCredentials: true });
      toast.success(`${p.name} updated — live on the site now.`);
      refresh();
    } catch (e) {
      toast.error("Could not save product.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={card} data-testid="editor-product">
      <h3 className="font-display text-2xl font-medium tracking-tight text-ink">Product pages</h3>
      <p className="mt-1 text-xs text-ink/50">Edit a product's name, code, description, specs and photos. Blank fields keep the built-in values.</p>
      <select value={pid} onChange={(e) => select(e.target.value)} data-testid="product-select" className={`${inputCls} mt-5 cursor-pointer`}>
        <option value="">Choose a product…</option>
        {CATEGORIES.filter((c) => c !== "All").map((c) => (
          <optgroup key={c} label={c}>
            {products.filter((x) => x.category === c).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} {x.sku ? `(${x.sku})` : ""}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {p && (
        <div className="mt-6 space-y-5" data-testid="product-form">
          <div className="grid gap-3 md:grid-cols-2">
            <input className={inputCls} value={form.name} placeholder={p.name} data-testid="product-name-input" onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className={inputCls} value={form.sku} placeholder={p.sku || "Product code"} data-testid="product-sku-input" onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          </div>
          <textarea className={`${inputCls} !rounded-2xl`} rows={3} value={form.blurb} placeholder={p.blurb || "Description"} data-testid="product-blurb-input" onChange={(e) => setForm({ ...form, blurb: e.target.value })} />

          <div>
            <div className="flex items-center justify-between">
              <p className="text-[13px] font-semibold text-ink">Spec rows {specsOn ? "(custom)" : "(using defaults)"}</p>
              {!specsOn ? (
                <button type="button" data-testid="specs-customise-btn" className={btnGhost} onClick={() => { setSpecsOn(true); setSpecRows(p.extraSpecs || []); }}>
                  Customise
                </button>
              ) : (
                <button type="button" data-testid="specs-add-row" className={btnGhost} onClick={() => setSpecRows([...specRows, { l: "", v: "" }])}>
                  <Plus className="h-3.5 w-3.5" /> Add row
                </button>
              )}
            </div>
            {specsOn && (
              <div className="mt-3 space-y-2">
                {specRows.map((r, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`spec-row-${i}`}>
                    <input className={inputCls} value={r.l} placeholder="Label (e.g. Size)" onChange={(e) => setSpecRows(specRows.map((x, xi) => (xi === i ? { ...x, l: e.target.value } : x)))} />
                    <input className={inputCls} value={r.v} placeholder="Value" onChange={(e) => setSpecRows(specRows.map((x, xi) => (xi === i ? { ...x, v: e.target.value } : x)))} />
                    <button type="button" className={`${btnGhost} !px-3.5 shrink-0`} onClick={() => setSpecRows(specRows.filter((_, xi) => xi !== i))} aria-label="Remove row">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <p className="text-[13px] font-semibold text-ink">Photos</p>
            <PhotoSlot
              label="Master photo (used in listings &amp; as default)"
              testId="photo-main"
              current={images.image ?? p.image}
              isCustom={!!images.image}
              onUpload={(url) => setImg("image", url)}
              onReset={() => setImg("image", undefined)}
              pool={pool}
              onPick={(u) => setImg("image", u)}
            />
            {p.finishImages && (p.finishes || []).length > 1 && (
              <p className="-mt-1 text-[11px] leading-relaxed text-ink/45">
                Tip: on the product page, the hero photo is the first finish's photo ({p.finishes[0]}) — set it from that slot below. The master photo is used in product cards and listings.
              </p>
            )}
            {(p.finishes || []).filter((f) => p.finishImages?.[f] || images.finishImages?.[f]).map((f) => (
              <PhotoSlot
                key={f}
                label={`${f} photo`}
                testId={`photo-finish-${f.toLowerCase().replace(/\s+/g, "-")}`}
                current={(images.finishImages?.[f] ?? p.finishImages?.[f]) || null}
                isCustom={!!images.finishImages?.[f]}
                onUpload={(url) => setFinishImg(f, url)}
                onReset={() => setFinishImg(f, null)}
                pool={pool}
                onPick={(u) => setFinishImg(f, u)}
              />
            ))}
            {(p.finishes || []).filter((f) => p.finishGalleries?.[f] || images.finishGalleries?.[f]).map((f) => (
              <GalleryEditor
                key={f}
                label={`${f} gallery`}
                testId={`gallery-${f.toLowerCase().replace(/\s+/g, "-")}`}
                photos={images.finishGalleries?.[f] ?? p.finishGalleries?.[f] ?? []}
                onChange={(arr) => setFinishGal(f, arr)}
                pool={pool}
              />
            ))}
            {p.galleryImages !== undefined || images.galleryImages ? (
              <GalleryEditor
                label="Gallery photos"
                testId="gallery-shared"
                photos={images.galleryImages ?? p.galleryImages ?? []}
                onChange={(arr) => setImg("galleryImages", arr)}
                pool={pool}
              />
            ) : null}
            {p.techImage || images.techImage ? (
              <PhotoSlot
                label="Dimension drawing"
                testId="photo-tech"
                current={images.techImage ?? p.techImage}
                isCustom={!!images.techImage}
                onUpload={(url) => setImg("techImage", url)}
                onReset={() => setImg("techImage", undefined)}
              />
            ) : null}
            {(p.finishes || []).length > 1
              ? p.finishes.map((f) => (
                  <PdfSlot
                    key={f}
                    label={`Detailed Spec Sheet — ${f}`}
                    testId={`specpdf-${f.toLowerCase().replace(/\s+/g, "-")}`}
                    value={specPdfs[f] || null}
                    onUpload={(v) => setSpecPdfs({ ...specPdfs, [f]: v })}
                    onRemove={() => {
                      const m = { ...specPdfs };
                      delete m[f];
                      setSpecPdfs(m);
                    }}
                  />
                ))
              : (
                <PdfSlot
                  label="Detailed Spec Sheet (PDF)"
                  testId="specpdf"
                  value={specPdf}
                  onUpload={setSpecPdf}
                  onRemove={() => setSpecPdf(null)}
                />
              )}
          </div>

          <button onClick={save} disabled={saving} data-testid="product-save-btn" className={btnInk}>
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : "Save product changes"}
          </button>
          {ov.updated_by && (
            <p className="text-[11px] text-ink/40">Last edited by {ov.updated_by} · {new Date(ov.updated_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</p>
          )}
        </div>
      )}
    </div>
  );
};

const AdminContent = () => (
  <div className="mt-10 space-y-8" data-testid="admin-content">
    <HeroEditor />
    <TileEditor />
    <FeaturedEditor />
    <ProductEditor />
    <p className="text-xs leading-relaxed text-ink/40">
      Everything you save here is stored in the database and cloud storage — it survives every future website update automatically.
    </p>
  </div>
);

export default AdminContent;
